/**
 * truckParts.ts (PARTS-PICKUP-01) — the shared "what parts does this call
 * need" brain. Moved out of the Parts for the Day report so the office panel,
 * the report and the tech's phone all read the SAME parser (they had drifted:
 * the phone parsed notes only, the report honored structured parts too).
 */
// Detect a tune-up + its kind from dispatch notes (same signal the agreement
// visit-history uses: "PLUS FRN CK" / "AC CK").
export function tuneupType(notes: string | null): "Furnace tune-up" | "A/C tune-up" | "Boiler tune-up" | null {
  const h = (notes ?? "").toUpperCase();
  if (/\bFRN\s*CK|FURNACE\s*CK|\bHEAT\s*CK|FURNACE\s*TUNE|\bFRN\s*TUNE/.test(h)) return "Furnace tune-up";
  if (/\bA\/?C\s*CK|COOL\s*CK|\bAC\s*CK|\bA\/?C\s*TUNE|\bAC\s*TUNE|COOLING\s*TUNE/.test(h)) return "A/C tune-up";
  if (/BOILER\s*CK|BOILER\s*CHECK|BOILER\s*TUNE/.test(h)) return "Boiler tune-up";
  return null;
}

// ── Real parts, read from the dispatch note ─────────────────────────────────
// (Exported for Pegasus Field's "Parts for the Day" — same brain, phone-sized.)
// "gear" = truck equipment the note calls out (ladders, 2-person jobs) — the
// Andrew/5712 Tucker Lane fix (Seth 2026-08-26: "Need two people and 30 foot
// ladder" was invisible because the parser only knew filters and pads).
export type PartKind = "filter" | "aprilaire" | "cabinet" | "humidifier" | "part" | "gear";
export interface TruckPart { key: string; label: string; kind: PartKind; qty?: number }

const MIN_DIM = 8, MAX_DIM = 32;       // valid HVAC filter dimensions (kills "92x09" / phone / time noise)
const CAB = "F(?:25|35|50|100|150|200|250|300)"; // media-cabinet model — part of a filter's FULL name
const HUMIDIFIER_PADS = new Set(["APR10", "APR12", "APR35", "APR45"]); // Aprilaire water panels = the humidifier pad

/**
 * Pull the parts a tech should grab from a call's notes. Patterns mined from
 * 123k dispatch notes. A filter's FULL NAME is its size + cabinet — "20x25 F100"
 * is ONE filter, not a size plus a cabinet. Aprilaire codes split into
 * humidifier pads (APR35…) and air-cleaner filters (APR213…). "HUMI" is just a
 * flag that the plan includes a humidifier, so a generic pad is only added when
 * no specific pad is named. Dimension-validated + word-boundary-anchored so
 * model #s (F33UX305) and phone numbers don't masquerade as parts.
 */
export function extractTruckParts(notes: string | null): TruckPart[] {
  if (!notes) return [];
  const out = new Map<string, TruckPart>();
  const add = (key: string, label: string, kind: PartKind) => { if (!out.has(key)) out.set(key, { key, label, kind }); };
  // Filter size, optionally followed by its cabinet model → ONE filter ("20x25 F100").
  for (const m of notes.matchAll(/\b(\d{1,2})\s*[xX]\s*(\d{1,2})(?:\s*[xX]\s*(\d))?\b/g)) {
    const w = +m[1], h = +m[2], d = m[3] ? +m[3] : null;
    if (w < MIN_DIM || w > MAX_DIM || h < MIN_DIM || h > MAX_DIM) continue;
    if (d !== null && (d < 1 || d > 6)) continue;
    const size = d ? `${w}x${h}x${d}` : `${w}x${h}`;
    const tail = notes.slice((m.index ?? 0) + m[0].length, (m.index ?? 0) + m[0].length + 8);
    const cab = tail.match(new RegExp(`^\\s+(${CAB})\\b`, "i"));
    const label = cab ? `${size} ${cab[1].toUpperCase()}` : size;
    add(`SZ:${label.toUpperCase()}`, label, "filter");
  }
  // Aprilaire codes — humidifier water panels vs air-cleaner filters.
  let hasPad = false;
  for (const m of notes.matchAll(/\bAPR\s?(\d{2,4})\b/gi)) {
    const code = `APR${m[1]}`;
    const pad = HUMIDIFIER_PADS.has(code);
    add(code, code, pad ? "humidifier" : "aprilaire");
    if (pad) hasPad = true;
  }
  // Humidifier pad — only when the note CALLS ONE OUT (Seth 2026-09-01, Brent /
  // Wells Concrete: "is humid in the building" was matching HUMI\w* and
  // flagging a pad on an air-handler call. "Right now it's… guessing").
  // What counts: ESC's shorthand token "HUMI" as its own word ("(3) HUMI",
  // "HUMI CK" — tune-up plan lines), or an explicit "humidifier pad" /
  // "humi pad" / "water panel". Plain English "humid" / "humidity" /
  // "humidifier" alone never flags.
  if (
    !hasPad &&
    (/\bHUMI\b/i.test(notes) ||
      /\bhumid(?:ifier)?\s*pads?\b|\bwater\s*panel\b/i.test(notes))
  )
    add("HUMI", "Humidifier pad", "humidifier");
  // GEAR the note calls out. Ladder with a height ("30 foot ladder", "24' ext
  // ladder") → sized chip; bare "ladder" still flags. Word-boundary anchored.
  const lad = notes.match(/\b(\d{1,2})\s*(?:['’]|\s*-?\s*(?:ft|foot|feet))?\s+(?:ext(?:ension)?\s+)?ladder\b/i);
  if (lad) add(`LADDER:${lad[1]}`, `${lad[1]}' ladder`, "gear");
  else if (/\bladders?\b/i.test(notes)) add("LADDER", "Ladder", "gear");
  // Crew callout — "need two people" / "2 man" / "two techs" = plan the pair.
  if (/\b(?:two|2)[\s-]*(?:people|person|man|men|techs?|guys)\b/i.test(notes)) {
    add("CREW2", "2-person job", "gear");
  }
  return [...out.values()];
}

export const PART_CHIP: Record<PartKind, string> = {
  filter: "border-sky-200 bg-sky-50 text-sky-700",
  aprilaire: "border-violet-200 bg-violet-50 text-violet-700",
  cabinet: "border-slate-200 bg-slate-100 text-slate-600",
  humidifier: "border-cyan-200 bg-cyan-50 text-cyan-700",
  part: "border-emerald-200 bg-emerald-50 text-emerald-700",
  gear: "border-amber-300 bg-amber-50 text-amber-800",
};
export const KIND_ORDER: Record<PartKind, number> = { filter: 0, aprilaire: 1, cabinet: 2, humidifier: 3, part: 4, gear: 5 };
